/**
 * ProfileBio — tappable, editable bio under the name.
 * Web port of the bio logic in client-native ProfileScreen.tsx.
 *
 * Default derived from onboarding `whyDoYouWantToLearn` (localStorage
 * "onboardingData" — same key iOS uses). User edits persist to localStorage
 * key "profile_bio" (iOS MMKV PROFILE_BIO_KEY). Max 140 chars, newlines → spaces.
 */
import { FC, useState } from "react";
import { colors } from "@/ui";
import type { LearningReason } from "@shared_types";

const PROFILE_BIO_KEY = "profile_bio";
const ONBOARDING_STORAGE_KEY = "onboardingData";

const learningReasonToBio: Record<LearningReason, string> = {
  travel: "exploring the world one word at a time ✈️",
  work: "leveling up my career with Spanish 💼",
  friends_and_family: "connecting with mis amigos y familia 👯",
  school: "vibing to Bad Bunny in Spanish 🐰",
  self_improvement: "becoming the best version of myself 📈",
  romance: "learning the language of love ❤️",
  other: "on my Spanish journey 🦜",
  no_value: "on my Spanish journey 🦜",
};

function deriveDefaultBio(): string {
  const saved = localStorage.getItem(PROFILE_BIO_KEY);
  if (saved) return saved;
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const reason = parsed.whyDoYouWantToLearn as LearningReason;
      return learningReasonToBio[reason] || learningReasonToBio.no_value;
    }
  } catch {
    // ignore parse errors
  }
  return learningReasonToBio.no_value;
}

const ProfileBio: FC = () => {
  // Lazy initializer reads the persisted bio synchronously on mount — no effect,
  // no empty-string first render.
  const [bio, setBio] = useState(() => deriveDefaultBio());
  const [editing, setEditing] = useState(false);

  const commit = () => {
    setEditing(false);
    if (bio.trim()) localStorage.setItem(PROFILE_BIO_KEY, bio);
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={bio}
        maxLength={140}
        placeholder="Add a bio..."
        onChange={(e) => setBio(e.target.value.replace(/\n/g, " ").slice(0, 140))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        className="text-sm"
        style={{
          textAlign: "center",
          color: colors.primary,
          border: "none",
          outline: "none",
          background: "transparent",
          width: "100%",
          maxWidth: 280,
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-sm"
      style={{
        color: colors.primary,
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "center",
        padding: 0,
      }}
    >
      {bio || "Add a bio..."}
    </button>
  );
};

export default ProfileBio;
